// @tennis-matches/services/governance/auto-provision-player-role.service
// Self-Service: Beim Login automatisch ein role_binding (role=player, scope=tenant)
// anlegen, wenn die User-E-Mail bereits in public.club_people (für einen aktiven Tenant)
// hinterlegt ist und der User für diesen Tenant noch keine aktive Rolle hat.
//
// Damit landet eine vom Vereinsadmin pre-provisionierte Person direkt im Hub
// (ohne Approval/Pending), mit Default-Rolle „Spieler" (volle Lese-/Bezahl-Sicht).

import { getSupabaseClient } from "../supabase/index.js";

function __asStr(x) {
  return String(x ?? "").trim();
}

function __isMissingRpcError(error) {
  const code = __asStr(error?.code);
  const status = Number(error?.status || 0);
  const msg = __asStr(error?.message).toLowerCase();
  return (
    status === 404 ||
    code === "PGRST202" ||
    code === "42883" ||
    msg.includes("could not find") ||
    msg.includes("schema cache")
  );
}

/**
 * Ruft `public.auto_provision_player_role_for_self()` auf.
 * Best-effort — alle Fehler sind nicht-fatal für den Login-Flow.
 *
 * @returns {Promise<
 *   | { ok: true, provisionedCount: number, provisioned: Array<{ tenant_id: string, role: string, role_binding_id: string }>, email?: string }
 *   | { ok: false, reason: string, missing?: boolean, error?: unknown }
 * >}
 */
export async function autoProvisionPlayerRoleRemote() {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  let rpcRes = await supabase.rpc("auto_provision_player_role_for_self");
  if (rpcRes?.error && __isMissingRpcError(rpcRes.error)) {
    // Fallback: direkt im app_config-Schema versuchen.
    if (typeof supabase.schema === "function") {
      rpcRes = await supabase.schema("app_config").rpc("auto_provision_player_role_for_self");
    }
  }

  const { data, error } = rpcRes || {};
  if (error) {
    if (__isMissingRpcError(error)) return { ok: false, reason: "rpc-missing", missing: true };
    return { ok: false, reason: "rpc-error", error };
  }

  const payload = data && typeof data === "object" ? data : {};
  if (payload.ok === false) {
    return { ok: false, reason: __asStr(payload.reason) || "rpc-failed" };
  }

  const provisioned = Array.isArray(payload.provisioned) ? payload.provisioned : [];
  return {
    ok: true,
    provisionedCount: Number(payload.provisioned_count) || provisioned.length,
    provisioned,
    email: __asStr(payload.email) || undefined,
  };
}
